import type { FC } from 'react'
import { useEffect } from 'react'

import { AppointmentLobby } from '@liveHelper/components/AppointmentLobby'
import { Chatting } from '@liveHelper/components/Chatting'
import { CustomerSurvey } from '@liveHelper/components/CustomerSurvey'
import { Dialing } from '@liveHelper/components/Dialing'
import { Idle } from '@liveHelper/components/Idle'
import { usePersistLastEngagedConversation } from '@liveHelper/components/LiveHelperComponent/usePersistLastEngagedConversation'
import { OnCall } from '@liveHelper/components/OnCall'
import { RequestMicrophoneAccess } from '@liveHelper/components/RequestMicrophoneAccess'
import { RequestVisitorInformation } from '@liveHelper/components/RequestVisitorInformation'
import { Scheduling } from '@liveHelper/components/Scheduling'
import { WidgetContainer } from '@liveHelper/components/shared/WidgetContainer'
import { useChatChannelConfig } from '@liveHelper/context/ChatChannelConfigContext'
import { DraggableContext } from '@liveHelper/context/DraggableContext'
import { useRedirectUrl } from '@liveHelper/context/VisitorChannelContext/useRedirectUrl'
import { useSendPageView } from '@liveHelper/context/VisitorChannelContext/useSendPageView'
import { useAgentRTCUser } from '@liveHelper/hooks/useAgentRTCUser'
import { useConversationPIP } from '@liveHelper/hooks/useConversationPIP'
import { useCrossTabStatePersist } from '@liveHelper/hooks/useCrossTabStatePersist'
import { useExternalControl } from '@liveHelper/hooks/useExternalControl'
import { useFeaturesEnablement } from '@liveHelper/hooks/useFeaturesEnablement'
import { useJoinVideoCallChannel } from '@liveHelper/hooks/useJoinVideoCallChannel'
import { usePublishVisitorLocalTracks } from '@liveHelper/hooks/usePublishVisitorLocalTracks'
import { useQueuePositionUpdate } from '@liveHelper/hooks/useQueuePositionUpdate'
import { useUpdateActiveStatus } from '@liveHelper/hooks/useUpdateActiveStatus'
import {
  actionsSelector,
  callDataSelector,
  fullScreenPreviewSelector,
  isWidgetVisibleSelector,
  paramsSelector,
  uiStateSelector,
  useStore,
} from '@liveHelper/models/store'
import {
  useTrackEmbedInstance,
  useTrackNetworkInfo,
} from '@liveHelper/tracking/useLiveHelperTracking'
import { useTrackOnCallProgress } from '@liveHelper/tracking/useTrackOnCallProgress'
import { FullScreenPreview } from '@oneToOne/shared/components/chat/FullScreenPreview'
import { GlobalCustomCSS } from '@oneToOne/shared/GlobalCustomCSS'

import { AgoraRTCClientProvider } from '@core/agoraRTC'
import { logDebug } from '@core/logging'
import { persistChatChannelId } from '@core/tracking/funnelTracking'

import { usePersistCall } from './usePersistCall'
import { usePersistConversation } from './usePersistConversation'

/**
 * Plain Live Helper component
 *
 * The `widgetVisible` state param is initially set to false and will be changed to true
 * once a phx_reply is received from the server. This prevents rendering the initial
 * idle state of the widget before receiving a response from the server. The server's
 * response may include call data to render states such as `on_call`, `ringing`, and `dialing`,
 * which should automatically restore the UI to the corresponding state.
 */
export const LiveHelperComponent: FC = () => {
  const params = useStore(paramsSelector)
  const callData = useStore(callDataSelector)
  const isWidgetVisible = useStore(isWidgetVisibleSelector)
  const chatChannelConfig = useChatChannelConfig()
  const fullScreenPreview = useStore(fullScreenPreviewSelector)
  const { hideFullScreenPreview } = useStore(actionsSelector)

  useCrossTabStatePersist()
  useUpdateActiveStatus()
  useExternalControl()
  useQueuePositionUpdate()

  const trackEmbedInstance = useTrackEmbedInstance()
  useEffect(() => {
    trackEmbedInstance()
  }, [trackEmbedInstance])

  useEffect(() => {
    if (params.chat_channel_id) {
      persistChatChannelId(params.chat_channel_id)
    } else {
      logDebug('No chat channel id')
    }
  }, [params.chat_channel_id])

  const showWidget = Boolean(
    chatChannelConfig && !params.silent_mode && isWidgetVisible,
  )

  useEffect(() => {
    if (window._fwn?.liveHelper) {
      window._fwn.liveHelper.showWidget = showWidget
    }
  }, [showWidget])

  if (showWidget) {
    return (
      <AgoraRTCClientProvider enableLogUpload={true} loadClient={!!callData}>
        <GlobalCustomCSS
          themeName={params.theme}
          bottomPadding={chatChannelConfig?.widget_style?.widget_bottom_padding}
        />
        {fullScreenPreview ? (
          <FullScreenPreview
            attachment={fullScreenPreview.attachment}
            onClose={hideFullScreenPreview}
          />
        ) : null}
        <DraggableContext>
          <WidgetContainer>
            <LiveHelperContent />
          </WidgetContainer>
        </DraggableContext>
      </AgoraRTCClientProvider>
    )
  }

  return null
}

/**
 * Live Helper Component Content. Renders the different UI States of the widget,
 */
const LiveHelperContent: FC = () => {
  const uiState = useStore(uiStateSelector)

  useJoinVideoCallChannel()
  // Only update persisted conversation when the widget is visible. This ensures
  // the visitor channel data is available before updating the persisted
  // conversation.
  usePersistConversation()
  usePersistLastEngagedConversation()
  usePersistCall()
  useTrackOnCallProgress()
  useConversationPIP()
  useRedirectUrl()
  useSendPageView()
  useFeaturesEnablement()

  const trackNetworkInfo = useTrackNetworkInfo()
  useEffect(() => {
    trackNetworkInfo()
  }, [trackNetworkInfo])

  const agentRTCUser = useAgentRTCUser()
  const localVideoTrack = usePublishVisitorLocalTracks()

  switch (uiState) {
    case 'idle_expanded':
    case 'idle_minimized':
      return <Idle isExpanded={uiState === 'idle_expanded'} />
    case 'idle_scheduling':
      return <Scheduling />
    case 'request_microphone_access':
      return <RequestMicrophoneAccess />
    case 'request_visitor_information':
      return <RequestVisitorInformation />
    case 'appointment_lobby':
    case 'appointment_dialing':
      return <AppointmentLobby isDialing={uiState === 'appointment_dialing'} />
    case 'chatting':
    case 'chatting_dialing':
    case 'chatting_ringing':
    case 'chatting_on_call':
      return (
        <Chatting
          agentRTCUser={agentRTCUser}
          localVideoTrack={localVideoTrack}
        />
      )
    case 'on_call_fullscreen':
    case 'on_call_expanded':
    case 'on_call_minimized':
      return (
        <OnCall
          isMinimized={uiState === 'on_call_minimized'}
          agentRTCUser={agentRTCUser}
          localVideoTrack={localVideoTrack}
        />
      )
    case 'dialing_minimized':
    case 'dialing_expanded':
      return <Dialing />
    case 'customer_survey':
      return <CustomerSurvey />
    default:
      throw new Error('Unknown state: ' + uiState)
  }
}
